import { useState, useEffect, useRef } from 'react';
import { initFieldState } from './constants';
import { FieldState } from '../types';
import { convertToFixed } from '../../utils/numbers';
import { EngineData } from '../../context/EventsManagerContext';
import { InspectionTripType } from '../../types/types';
import { Submission } from '../../types/submission';

export const VehicleFormFields = {
  odometer: { name: 'odometer', optional: false },
  odometerIsoUom: { name: 'odometerIsoUom', optional: false },
  engineHours: { name: 'engineHours', optional: false },
  inspectionTripType: { name: 'inspectionTripType', optional: false },
};

export const VehicleFormLabels = {
  [VehicleFormFields.odometer.name]: 'cloud_dvir__odometer_reading',
  [VehicleFormFields.engineHours.name]: 'cloud_dvir__engine_hours',
  [VehicleFormFields.inspectionTripType.name]: 'cloud_dvir__inspection_trip_type',
};

export const InspectionTripTypeLabels = {
  PRE_TRIP: 'cloud_dvir__pre_trip',
  POST_TRIP: 'cloud_dvir__post_trip',
  OTHER: 'cloud_dvir__ad_hoc',
};

export const OdometerIsoUomLabels = {
  SMI: 'cloud_dvir__units_miles_abbr',
  KMT: 'cloud_dvir__units_kilometers_abbr',
};

export type VehicleFormFieldState = FieldState<string>;

export type VehicleForm = Record<keyof typeof VehicleFormFields, VehicleFormFieldState>;

interface UseVehicleFormProps {
  engineData: EngineData;
  inspectionTripType: InspectionTripType;
  defaultOdometerIsoUom: string;
  dvirData: Submission;
}

interface UseVehicleForm {
  form: VehicleForm;
  validate: () => boolean;
  handleFormChange: (name: string, value: string) => void;
  hasFormChanged: () => boolean,
}

/** Real EDC reading (not missing / default zero / rounds to 0.00). */
const hasEdcValue = (value: string | number | null | undefined): boolean => {
  if (value == null || value === '') {
    return false;
  }

  const num = Number(String(value).replace(',', '.'));

  // Ignore 0 and values that display as 0.00 after 2-decimal formatting.
  return Number.isFinite(num) && Math.abs(num) >= 0.01;
};

const isEmptyRequiredValue = (value: string | null | undefined): boolean => (
  value == null || String(value).trim() === ''
);

/** Empty, non-numeric, or 0 / 0.00 — not allowed for odometer/engine hours. */
const isInvalidRequiredNumericValue = (value: string | null | undefined): boolean => {
  if (isEmptyRequiredValue(value)) {
    return true;
  }

  return !hasEdcValue(value);
};

const transformInitForm = ({ inspectionTripType, defaultOdometerIsoUom }: UseVehicleFormProps):VehicleForm => Object.values(VehicleFormFields)
  .reduce((form, field) => {
    let fieldValue = initFieldState.value;

    if (field.name === VehicleFormFields.inspectionTripType.name) {
      fieldValue = inspectionTripType;
    }

    if (field.name === VehicleFormFields.odometerIsoUom.name) {
      fieldValue = defaultOdometerIsoUom;
    }

    return {
      ...form,
      [field.name]: {
        ...initFieldState,
        value: fieldValue,
      },
    };
  }, {} as VehicleForm);

const useVehicleForm = (props: UseVehicleFormProps): UseVehicleForm => {
  const { engineData, dvirData } = props;

  const [form, setForm] = useState<VehicleForm>(() => transformInitForm(props));

  const isFormDirty = useRef<boolean>(false);

  const userEditedFields = useRef<Set<string>>(new Set());

  const isEngineDataField = (name: string) => [VehicleFormFields.odometer.name, VehicleFormFields.engineHours.name].includes(name);

  const validate = (): boolean => {
    let isValid = true;

    const validatedForm = Object.entries(form).reduce((res, [name, field]) => {
      const fieldDescription = VehicleFormFields[name];

      if (!fieldDescription.optional) {
        const isInvalid = isEngineDataField(name)
          ? isInvalidRequiredNumericValue(field.value)
          : isEmptyRequiredValue(field.value);

        if (isInvalid) {
          isValid = false;

          return {
            ...res,
            [name]: { ...field, isError: true },
          };
        }
      }

      return { ...res, [name]: field };
    }, {} as VehicleForm);

    if (!isValid) {
      setForm(validatedForm);
    }

    return isValid;
  };

  const handleFormChange = (name: string, value: string) => {
    const updatedField: FieldState<string> = { value, isError: false };

    isFormDirty.current = true;

    if (isEngineDataField(name)) {
      userEditedFields.current.add(name);
    }

    setForm({ ...form, [name]: updatedField });
  };

  const updateFormFields = (
    data: Partial<Submission>,
    options?: { fromEdc?: boolean },
  ) => {
    setForm((prevForm) => Object.entries(prevForm).reduce((result, [name, field]) => {
      const rawValue = data[name];

      if (userEditedFields.current.has(name)) {
        return { ...result, [name]: field };
      }

      // EDC: use a real reading; leave empty when missing or default zero.
      if (options?.fromEdc && isEngineDataField(name)) {
        if (!hasEdcValue(rawValue)) {
          return { ...result, [name]: field };
        }

        const value = convertToFixed(rawValue, 2);

        if (!hasEdcValue(value)) {
          return { ...result, [name]: field };
        }

        return {
          ...result,
          [name]: { ...field, value, isError: false },
        };
      }

      if (!rawValue) {
        return { ...result, [name]: field };
      }

      const value = isEngineDataField(name) ? convertToFixed(rawValue, 2) : rawValue;

      return {
        ...result,
        [name]: { ...field, value, isError: false },
      };
    }, {} as VehicleForm));
  };

  useEffect(() => {
    if (hasEdcValue(engineData?.engineHours) || hasEdcValue(engineData?.odometer)) {
      updateFormFields(engineData, { fromEdc: true });
    }
  }, [engineData]);

  useEffect(() => {
    if (dvirData) {
      updateFormFields(dvirData);
    }
  }, [dvirData]);

  return {
    form,
    validate,
    handleFormChange,
    hasFormChanged: () => isFormDirty.current,
  };
};

export default useVehicleForm;
